import { useEffect, useState } from 'react'
import QRCode from 'qrcode'
import { useAuth } from './AuthProvider'
import { buildVcard, vcardFilename, type CardIdentity } from './vcard'
import './BusinessCard.css'

/**
 * The digital business card.
 *
 * A dark full-screen surface holding your photo, name, title, company, email,
 * phone, and a QR code that saves all of it to whoever scans it. Meant to be
 * held up across a table, so it takes the whole screen rather than floating in
 * a scrim — a phone-shaped card inside a phone is a smaller QR code for no
 * reason, and the QR is the part that has to be scannable from a metre away.
 *
 * Sized in viewport units throughout, so it fills a tall phone and still fits a
 * laptop in a browser window without scrolling.
 *
 * ## The QR code is generated here, not fetched
 *
 * The prototype builds the image URL against `api.qrserver.com`, which means
 * every time the card opens, a rep's name, job title, employer, mobile number
 * and work email are sent to a third party as a query string — logged in their
 * access log, and in any proxy in between. For a prototype that is the sensible
 * shortcut. For the thing a salesperson actually holds up, it is a needless
 * disclosure of the one payload on the screen that is personal data.
 *
 * It also fails in exactly the situation the card is for: a trade-fair hall
 * with no usable signal renders a broken image where the QR should be.
 *
 * Generated locally with `qrcode`. Same picture, no request.
 *
 * ## Error correction level M
 *
 * A vCard of this size is comfortably inside a version-6-ish symbol at M, which
 * tolerates ~15% damage — enough for a phone camera reading a screen at an
 * angle under hall lighting. H would tolerate more and pack the modules tighter,
 * which is the wrong trade on a small display.
 */
export function BusinessCard({ onClose }: { onClose: () => void }) {
  const { user } = useAuth()
  const [qr, setQr] = useState<string | null>(null)
  const [shared, setShared] = useState<string | null>(null)

  const identity: CardIdentity = {
    name: user?.displayName ?? '',
    title: user?.title ?? '',
    // Falls back rather than sitting empty: everyone using this tool works at
    // the same company, and a card that omits it is a worse default than one
    // that assumes correctly.
    company: user?.company?.trim() || 'Skida',
    phone: user?.phone ?? '',
    email: user?.email ?? '',
  }

  const vcard = buildVcard(identity)

  useEffect(() => {
    let live = true
    QRCode.toDataURL(vcard, {
      errorCorrectionLevel: 'M',
      margin: 1,
      width: 320,
      color: { dark: '#000B38', light: '#FFFFFF' },
    })
      .then((url) => live && setQr(url))
      .catch(() => live && setQr(null))
    return () => {
      live = false
    }
  }, [vcard])

  const share = async () => {
    // charset declared explicitly. The Blob encodes UTF-8 either way, but a
    // receiving address book that assumes Latin-1 turns "Sørgård" into mojibake
    // — and Norwegian names are the normal case here, not the edge one.
    const file = new File([vcard], vcardFilename(identity.name), {
      type: 'text/vcard;charset=utf-8',
    })

    // Three routes, narrowing. A phone shares the file itself, which lands in
    // the recipient's address book in one tap. A desktop browser with the Share
    // API but no file support gets the text. Everything else downloads the
    // .vcf, which is what a laptop was always going to do anyway.
    try {
      if (navigator.canShare?.({ files: [file] })) {
        await navigator.share({ files: [file], title: identity.name })
        return
      }
      if (navigator.share) {
        await navigator.share({
          title: identity.name,
          text: [identity.name, identity.title, identity.phone, identity.email]
            .filter(Boolean)
            .join('\n'),
        })
        return
      }
    } catch (e) {
      // A cancelled share throws AbortError. That is the user changing their
      // mind, not a failure, and must not fall through to a download they did
      // not ask for.
      if (e instanceof DOMException && e.name === 'AbortError') return
    }

    const href = URL.createObjectURL(file)
    const a = document.createElement('a')
    a.href = href
    a.download = file.name
    a.click()
    URL.revokeObjectURL(href)
    setShared('Saved as a .vcf file — send it as an attachment.')
  }

  const initials =
    identity.name
      .split(/\s+/)
      .filter(Boolean)
      .slice(0, 2)
      .map((p) => p.charAt(0).toUpperCase())
      .join('') || '?'

  // The card is only useful if it carries a way to reach you. Said plainly
  // rather than shown as a card with two blank lines on it.
  const missing = [
    !identity.phone.trim() && 'phone number',
    !identity.title.trim() && 'job title',
  ].filter(Boolean) as string[]

  return (
    <div className="bcard" role="dialog" aria-modal="true" aria-label="Digital business card">
      <div className="bcard__top">
        <img className="bcard__logo" src="/skida-logo-white.png" alt="Skida" />
        <div className="bcard__tools">
          <button className="bcard__tool" onClick={() => void share()} aria-label="Share">
            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="#fff"
                 strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
              <circle cx="18" cy="5" r="3" />
              <circle cx="6" cy="12" r="3" />
              <circle cx="18" cy="19" r="3" />
              <line x1="8.6" y1="10.6" x2="15.4" y2="6.4" />
              <line x1="8.6" y1="13.4" x2="15.4" y2="17.6" />
            </svg>
          </button>
          {/* The design's Download QR. A card held up works across a table;
              a PNG works on a printed stand, a slide, or an email signature —
              which is where most of these actually get scanned. */}
          {qr && (
            <a
              className="bcard__tool"
              href={qr}
              download={`${vcardFilename(identity.name).replace(/\.vcf$/, '')}-qr.png`}
              aria-label="Download QR"
              title="Download QR"
            >
              ⭳
            </a>
          )}
          <button className="bcard__tool" onClick={onClose} aria-label="Close">
            ✕
          </button>
        </div>
      </div>

      <div className="bcard__body">

        {user?.photoUrl ? (
          <img className="bcard__photo bcard__photo--img" src={user.photoUrl} alt="" />
        ) : (
          <div className="bcard__photo" aria-hidden="true">
            {initials}
          </div>
        )}

        <div className="bcard__who">
          <div className="bcard__name">{identity.name || 'Add your name'}</div>
          <div className="bcard__role">
            {[identity.title, identity.company].filter(Boolean).join(' · ')}
          </div>
        </div>

        <div className="bcard__rule" />

        <div className="bcard__lines">
          {identity.email && (
            <div className="bcard__line">
              <span aria-hidden="true">✉</span>
              {identity.email}
            </div>
          )}
          {identity.phone && (
            <div className="bcard__line">
              <span aria-hidden="true">☎</span>
              {identity.phone}
            </div>
          )}
        </div>

        <div className="bcard__qrbox">
          {qr ? (
            <img className="bcard__qr" src={qr} alt="Scan to save this contact" />
          ) : (
            <div className="bcard__qr bcard__qr--empty" />
          )}
        </div>
        <div className="bcard__scan">Scan to save this contact</div>

        {missing.length > 0 && (
          <p className="bcard__missing">
            No {missing.join(' and no ')} yet — add {missing.length > 1 ? 'them' : 'it'} under Edit
            profile.
          </p>
        )}
        {shared && <p className="bcard__shared">{shared}</p>}
      </div>
    </div>
  )
}
